import Vue from 'vue';
import { createDecorator } from 'vue-class-component';

/**
 * Custom state decorator, used to map vuex state
 * keys into component computed properties.
 * 
 * @State() stateKey!: any;
 * 
 * When used as a setter (i.e. stateKey = value) then it will
 * act as a mutation, using the assigned value as the payload
 * and the state key as the mutation name.
 * 
 * You can also pass a function to add a watcher for
 * the property:
 * 
 * @State((newValue, oldValue) => console.log(newValue, oldValue)) stateKey!: any;
 * 
 * If you need to access `this` inside of your watcher, then you have to
 * avoid the arrow syntax to allow the proper binding of this. Also,
 * to have TypeScript recognize your component type in your watcher,
 * annotate the decorator with your component type:
 * 
 * @State<Component>(function(oldValue, newValue) { console.log(this); }) stateKey!: any;
 */

export default function State<V extends Vue>(watcher?: (this: V, newValue: any, oldValue: any) => void) {
  return createDecorator((componentOptions, key) => {

    // Ensure the component has a computed object
    if (!componentOptions.computed) componentOptions.computed = {};

    // Create computed property for the given key
    componentOptions.computed[key] = {
      get: function() {
        return (this as any).$store.state[key];
      },
      set: function(payload) {
        (this as any).$store.commit(key, payload);
      }
    };

    // Operate on decorator extras
    if (watcher) {

      // Ensure the component has a watcher object
      if (!componentOptions.watch) componentOptions.watch = {};

      // Create watcher with properly bound this
      componentOptions.watch[key] = function(newValue, oldValue) {
        watcher.bind(this as any, newValue, oldValue)();
      };
    }
  });
}
